<script setup>
import { onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import UsageStat from './UsageStat.vue';
import UsageChart from './UsageChart.vue';
import UsageRanking from './UsageRanking.vue';
import { useProductUsage, PERIODS } from '../useProductUsage';

// Painel de uso dos produtos da org. Só entra em tela para administradores: as
// duas APIs por trás (reports v2 e a página Atendimento) negam o atendente, então
// para os demais papéis isto não renderiza meia tela — não renderiza nada.
defineProps({
  appCount: { type: Number, default: 0 },
});

const { t } = useI18n();

const {
  days,
  isLoading,
  failed,
  summary,
  messages,
  messagesDelta,
  aiMessages,
  conversationsTimeline,
  aiTimeline,
  aiRanking,
  delta,
  fetchAll,
} = useProductUsage();

onMounted(fetchAll);
watch(days, fetchAll);

// Os tokens de cor por produto vivem no CSS (claro/escuro têm passos próprios),
// e o canvas os lê resolvidos.
const CHAT_COLOR = '--pz-chart-series-chat';
const AI_COLOR = '--pz-chart-series-ai';
</script>

<template>
  <section class="flex flex-col gap-4">
    <header class="flex flex-wrap gap-3 justify-between items-center">
      <div>
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('OS_HOME.OVERVIEW.TITLE') }}
        </h2>
        <p class="mt-0.5 text-sm text-n-slate-11">
          {{ t('OS_HOME.OVERVIEW.SUBTITLE') }}
        </p>
      </div>

      <div
        class="flex flex-shrink-0 p-0.5 rounded-lg border border-n-weak bg-n-alpha-1"
      >
        <button
          v-for="period in PERIODS"
          :key="period"
          type="button"
          class="px-3 py-1 text-xs font-medium rounded-md transition-colors"
          :class="
            days === period
              ? 'bg-n-card text-n-slate-12 shadow-pz'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="days = period"
        >
          {{ t('OS_HOME.OVERVIEW.PERIOD', { days: period }) }}
        </button>
      </div>
    </header>

    <p v-if="failed" class="text-sm text-n-ruby-11">
      {{ t('OS_HOME.OVERVIEW.LOAD_ERROR') }}
    </p>

    <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <UsageStat
        :label="t('OS_HOME.OVERVIEW.STATS.CONVERSATIONS')"
        :value="isLoading ? null : (summary?.conversations_count ?? null)"
        :delta="delta('conversations_count')"
        :hint="t('OS_HOME.OVERVIEW.PRODUCTS.POWERCHAT')"
        icon="i-lucide-message-circle"
        icon-class="text-[var(--pz-chart-series-chat)]"
      />
      <UsageStat
        :label="t('OS_HOME.OVERVIEW.STATS.MESSAGES')"
        :value="isLoading ? null : messages"
        :delta="messagesDelta"
        :hint="t('OS_HOME.OVERVIEW.PRODUCTS.POWERCHAT')"
        icon="i-lucide-mails"
        icon-class="text-[var(--pz-chart-series-chat)]"
      />
      <UsageStat
        :label="t('OS_HOME.OVERVIEW.STATS.AI_MESSAGES')"
        :value="isLoading ? null : aiMessages"
        :hint="t('OS_HOME.OVERVIEW.PRODUCTS.AI')"
        icon="i-lucide-bot"
        icon-class="text-[var(--pz-chart-series-ai)]"
      />
      <UsageStat
        :label="t('OS_HOME.OVERVIEW.STATS.APPS')"
        :value="appCount"
        :hint="t('OS_HOME.OVERVIEW.STATS.APPS_HINT')"
        icon="i-lucide-layout-grid"
      />
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div class="p-4 rounded-pz border border-n-weak bg-n-card shadow-pz">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('OS_HOME.OVERVIEW.CHARTS.CONVERSATIONS') }}
        </h3>
        <p class="mt-0.5 mb-3 text-xs text-n-slate-10">
          {{ t('OS_HOME.OVERVIEW.PRODUCTS.POWERCHAT') }}
        </p>
        <UsageChart
          :timeline="conversationsTimeline"
          :color-var="CHAT_COLOR"
          :empty-label="
            isLoading
              ? t('OS_HOME.OVERVIEW.LOADING')
              : t('OS_HOME.OVERVIEW.EMPTY')
          "
        />
      </div>

      <div class="p-4 rounded-pz border border-n-weak bg-n-card shadow-pz">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('OS_HOME.OVERVIEW.CHARTS.AI_MESSAGES') }}
        </h3>
        <p class="mt-0.5 mb-3 text-xs text-n-slate-10">
          {{ t('OS_HOME.OVERVIEW.PRODUCTS.AI') }}
        </p>
        <UsageChart
          :timeline="aiTimeline"
          :color-var="AI_COLOR"
          :empty-label="
            isLoading
              ? t('OS_HOME.OVERVIEW.LOADING')
              : t('OS_HOME.OVERVIEW.EMPTY')
          "
        />
      </div>
    </div>

    <div class="p-4 rounded-pz border border-n-weak bg-n-card shadow-pz">
      <h3 class="mb-3 text-sm font-medium text-n-slate-12">
        {{ t('OS_HOME.OVERVIEW.CHARTS.AI_RANKING') }}
      </h3>
      <UsageRanking
        :entries="aiRanking"
        bar-class="bg-[var(--pz-chart-series-ai)]"
        :empty-label="
          isLoading
            ? t('OS_HOME.OVERVIEW.LOADING')
            : t('OS_HOME.OVERVIEW.EMPTY')
        "
      />
    </div>
  </section>
</template>
